import { useEffect, useState } from "react";
import { apiGetEmployeeRecord } from "../../../services/EmployeeRecordService";
import { erGetCPValue } from "../app-employee-records/employeeRecordUtils";
import { EmployeeRecord } from "winslow-core";
import { Maximize2 } from "lucide-react";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import "./DCEmployeeRecordProfileCard.css";
import { Icon } from "../../../ds2/components/Icon";
import { DCBaseProps } from "./DCBase";
import { useNavigate } from "react-router-dom";

type DCEmployeeRecordProfileCardProps = DCBaseProps & {
   data: {
    employeeRecordIds: string[];
   }    
}

export const DCEmployeeRecordProfileCard = ({message, data, isOutdated}: DCEmployeeRecordProfileCardProps) => {
    const navigate = useNavigate();
    
    const [employeeRecord, setEmployeeRecord] = useState<EmployeeRecord | null>(null);
    const [managerEmployeeRecord, setManagerEmployeeRecord] = useState<EmployeeRecord | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (data?.employeeRecordIds && data?.employeeRecordIds.length > 0) {
            (async () => {
                setLoading(true);
                try {
                    const record = await apiGetEmployeeRecord(data.employeeRecordIds[0]);
                    if (!record) {
                        toast("Unable to load employee record", toastFail);
                        return;
                    }
                    
                    setEmployeeRecord(record);

                    const managerId = erGetCPValue(record, "system-managerId");
                    if (managerId) {
                        const managerRecord = await apiGetEmployeeRecord(managerId);
                        if (managerRecord) {
                            setManagerEmployeeRecord(managerRecord);
                        }
                    }
                } catch (error) {
                    console.error("Error loading employee record:", error);
                    toast("Error loading employee record", toastFail);
                } finally {
                    setLoading(false);
                }
            })();
        }
    }, [data]);

    const handleExpand = () => {
        navigate(`/app/employee-records/view/${employeeRecord?.id}`);
    };

    const handleEmailClick = async () => {
        if (employeeRecord?.workEmail) {
            try {
                await navigator.clipboard.writeText(employeeRecord.workEmail);
                toast("Email copied to clipboard!", toastSuccess);
                
            } catch (err) {
                console.error('Failed to copy email:', err);
                toast("Failed to copy email", toastFail);
            }
        }
    };

    const getAvatarUrl = () => {
        return employeeRecord?.avatarURL || null;
    };

    const getFirstName = () => {
        return erGetCPValue(employeeRecord, "system-firstName") || "N/A";
    };

    const getLastName = () => {
        return erGetCPValue(employeeRecord, "system-lastName") || "N/A";
    };

    const getDepartment = () => {
        return erGetCPValue(employeeRecord, "system-department") || "N/A";
    };

    const getManagerFirstName = () => {
        return erGetCPValue(managerEmployeeRecord, "system-firstName") || "N/A";
    };

    const getManagerLastName = () => {
        return erGetCPValue(managerEmployeeRecord, "system-lastName") || "N/A";
    };

    const getManagerName = () => {
       return managerEmployeeRecord ? `${getManagerFirstName()} ${getManagerLastName()}` : "N/A";
    };

    const getManagerAvatarUrl = () => {
        return managerEmployeeRecord?.avatarURL;
    };

    const getWorkPhone = () => {
        const phone = erGetCPValue(employeeRecord, "system-workPhone") || "N/A";
        return formatPhoneNumber(phone);
    };

    const formatPhoneNumber = (phone: string) => {
        if (phone === "N/A" || !phone) return "N/A";
        
        // Remove all non-digit characters
        const cleaned = phone.replace(/\D/g, '');
        
        // Format as (XXX) XXX-XXXX for US numbers
        if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        
        // Return original if not a standard US number
        return phone;
    };


    const getInitials = () => {
        const firstName = getFirstName();
        const lastName = getLastName();
        if (firstName === "N/A" && lastName === "N/A") {
            return "?";
        }
        return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    };

    console.log("[DCEmployeeRecordProfileCard] data.employeeRecordId: ", data.employeeRecordId);

    // if (loading) {
    //     return (
    //         <div className="dc-employee-record-profile-card">
    //             <div className="dc-employee-record-profile-card-loading">
    //                 <div className="loading-spinner"></div>
    //                 <span>Loading employee profile...</span>
    //             </div>
    //         </div>
    //     );
    // }

    if (!employeeRecord) {
        return (
            <div className="dc-employee-profile-card">
                <div className="dc-employee-profile-card-error">
                    <span>Unable to load employee profile</span>
                </div>
            </div>
        );
    }

    return (
        <div className="dc-employee-record-profile-card">
            {/* Hero Section with Gradient Background */}
            <div className="dc-employee-record-profile-card-hero">
                <div className="hero-background"></div>
                <div className="hero-content">
                    <div className="dc-employee-record-profile-card-avatar">
                        {getAvatarUrl() ? (
                            <img 
                                src={getAvatarUrl()!} 
                                alt={`${getFirstName()} ${getLastName()}`}
                                className="dc-employee-record-profile-card-avatar-image"
                            />
                        ) : (
                            <div className="dc-employee-record-profile-card-avatar-placeholder">
                                {getInitials()}
                            </div>
                        )}
                    </div>
                    <div className="employee-name">
                        {getFirstName()} {getLastName()}
                    </div>
                </div>
                <div className="dc-employee-record-profile-card-actions">
                    <Icon
                        variant="primary"
                        onClick={handleExpand}
                        icon={<Maximize2 size={16} />}
                    />                        
                </div>
            </div>

            {/* Info Section with Two Columns */}
            <div className="dc-employee-record-profile-card-info">
                <div className="info-grid">
                    <div className="info-item">
                        <div className="info-label">Department</div>
                        <div className="info-value">{getDepartment()}</div>
                    </div>
                    <div className="info-item">
                        <div className="info-label">Manager</div>
                        <div className="info-value manager-info">
                            {getManagerAvatarUrl() && (
                                <img 
                                    src={getManagerAvatarUrl()} 
                                    alt={getManagerName()} 
                                    className="manager-avatar" 
                                />
                            )}
                            <span>{getManagerName()}</span>
                        </div>
                    </div>
                    <div className="info-item">
                        <div className="info-label">Phone</div>
                        <div className="info-value">{getWorkPhone()}</div>
                    </div>
                    <div className="info-item">
                        <div className="info-label">Email</div>
                        <div 
                            className="info-value copyable-email" 
                            onClick={handleEmailClick}
                            title="Click to copy email"
                        >
                            {employeeRecord.workEmail}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}